import { useState, useEffect, useCallback } from 'react';
import { pdaZoneOfflineService } from '../offline/pda-zone.service';
import { CachedPdaZoneSet, CachedPdaZone } from '../offline/db';
import { useTenantStore } from '@/store/tenant.store';
import { useIncidentStore } from '@/store/incident.store';
import { networkService } from '@/services/network.service';

interface UseOfflinePdaZonesResult {
  zoneSets: CachedPdaZoneSet[];
  zones: Record<string, CachedPdaZone[]>;
  isLoading: boolean;
  isOffline: boolean;
  pendingBreadcrumbs: number;
  loadZones: (zoneSetId: string) => Promise<void>;
  syncNow: () => Promise<{ synced: number; failed: number }>;
  refresh: () => Promise<void>;
}

export function useOfflinePdaZones(): UseOfflinePdaZonesResult {
  const { activeTenant } = useTenantStore();
  const { activeIncident } = useIncidentStore();

  const [zoneSets, setZoneSets] = useState<CachedPdaZoneSet[]>([]);
  const [zones, setZones] = useState<Record<string, CachedPdaZone[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!networkService.isOnline());
  const [pendingBreadcrumbs, setPendingBreadcrumbs] = useState(0);

  // Track online status
  useEffect(() => {
    const removeListener = networkService.addListener((connected) => {
      setIsOffline(!connected);
    });

    return () => {
      removeListener();
    };
  }, []);

  const updatePendingCount = useCallback(async () => {
    const count = await pdaZoneOfflineService.getPendingBreadcrumbCount();
    setPendingBreadcrumbs(count);
  }, []);

  const refresh = useCallback(async () => {
    if (!activeTenant) return;
    setIsLoading(true);
    try {
      if (networkService.isOnline()) {
        await pdaZoneOfflineService.cacheZonesForOffline(activeTenant.id, activeIncident?.id);
      }
      const cached = await pdaZoneOfflineService.getCachedZoneSets(activeIncident?.id);
      setZoneSets(cached);
      await updatePendingCount();
    } catch (err) {
      console.error('[useOfflinePdaZones] Failed to load:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeTenant, activeIncident, updatePendingCount]);

  const loadZones = useCallback(async (zoneSetId: string) => {
    const cached = await pdaZoneOfflineService.getCachedZones(zoneSetId);
    setZones((prev) => ({ ...prev, [zoneSetId]: cached }));
  }, []);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-sync breadcrumbs when coming online
  useEffect(() => {
    if (!isOffline && pendingBreadcrumbs > 0) {
      pdaZoneOfflineService.syncBreadcrumbs().then(() => {
        updatePendingCount();
      });
    }
  }, [isOffline, pendingBreadcrumbs, updatePendingCount]);

  const syncNow = useCallback(async () => {
    const result = await pdaZoneOfflineService.syncBreadcrumbs();
    await updatePendingCount();
    return result;
  }, [updatePendingCount]);

  return {
    zoneSets,
    zones,
    isLoading,
    isOffline,
    pendingBreadcrumbs,
    loadZones,
    syncNow,
    refresh,
  };
}
